import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Brand } from "@/components/site/brand";
import { DisclaimerBar } from "@/components/site/disclaimer-bar";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { MailCheck } from "lucide-react";

export const Route = createFileRoute("/verify-email")({
  head: () => ({
    meta: [
      { title: "Verify your email — CryptoBull Pro" },
      { name: "description", content: "Enter the verification code we sent to activate your CryptoBull Pro account." },
    ],
  }),
  component: VerifyEmail,
});

const RESEND_COOLDOWN_SECONDS = 60;
const RESEND_STORAGE_KEY = "verify_email_resend_at";

function VerifyEmail() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    const stored = typeof window !== "undefined" ? localStorage.getItem("pending_verify_email") : null;
    if (stored) setEmail(stored);
  }, []);

  useEffect(() => {
    if (!email) return;
    const update = () => {
      const raw = typeof window !== "undefined" ? localStorage.getItem(RESEND_STORAGE_KEY) : null;
      const nextAvailable = raw ? parseInt(raw, 10) : 0;
      const remaining = Math.max(0, Math.ceil((nextAvailable - Date.now()) / 1000));
      setSecondsLeft(remaining);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [email]);

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const verifyCode = async (codeToVerify: string) => {
    if (!email || codeToVerify.length < 6) {
      toast.error("Enter the 6-digit code from your email.");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.auth.verifyOtp({
      email,
      token: codeToVerify.trim(),
      type: "signup",
    });
    setSubmitting(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    // Activate profile + create welcome notification
    await supabase.rpc("mark_email_verified");
    if (typeof window !== "undefined") {
      localStorage.removeItem("pending_verify_email");
      localStorage.removeItem(RESEND_STORAGE_KEY);
    }
    toast.success("Welcome to CryptoBull Pro! Your account is now active.");
    navigate({ to: "/dashboard" });
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    await verifyCode(code);
  };

  // WebOTP API: auto-fill OTP from system (Android Chrome / supported browsers).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const otpCreds = (navigator as any).credentials;
    if (!otpCreds || !("OTPCredential" in window)) return;
    const ac = new AbortController();
    otpCreds
      .get({ otp: { transport: ["sms"] }, signal: ac.signal })
      .then((cred: any) => {
        if (cred?.code) {
          const digits = String(cred.code).replace(/\D/g, "").slice(0, 6);
          setCode(digits);
          if (digits.length === 6) void verifyCode(digits);
        }
      })
      .catch(() => {});
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const resend = async () => {
    if (!email) {
      toast.error("Enter your email first.");
      return;
    }
    if (secondsLeft > 0) return;
    setResending(true);
    const { error } = await supabase.auth.resend({ type: "signup", email });
    setResending(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    const nextAvailable = Date.now() + RESEND_COOLDOWN_SECONDS * 1000;
    if (typeof window !== "undefined") {
      localStorage.setItem(RESEND_STORAGE_KEY, String(nextAvailable));
    }
    setSecondsLeft(RESEND_COOLDOWN_SECONDS);
    toast.success("Verification code re-sent. Check your email.");
  };

  return (
    <div className="min-h-screen">
      <DisclaimerBar />
      <div className="px-4 py-10 flex flex-col items-center">
        <Brand size="lg" />
        <div className="mt-8 w-full max-w-md glass rounded-3xl p-7">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-10 w-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center">
              <MailCheck className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-lg font-semibold">Verify your email</h1>
              <p className="text-xs text-muted-foreground">Check your inbox for a 6-digit verification code.</p>
            </div>
          </div>
          <form className="space-y-4 mt-4" onSubmit={handleVerify}>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
            </div>
            <div className="space-y-2">
              <Label>Verification code</Label>
              <Input
                value={code}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setCode(digits);
                  if (digits.length === 6 && !submitting) {
                    void verifyCode(digits);
                  }
                }}
                inputMode="numeric"
                autoComplete="one-time-code"
                name="otp"
                maxLength={6}
                pattern="[0-9]{6}"
                placeholder="000000"
                className="tracking-[0.5em] text-center text-lg"
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? "Verifying…" : "Verify & activate account"}
            </Button>
          </form>
          <div className="mt-5 flex items-center justify-between text-xs text-muted-foreground">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={resend}
              disabled={resending || secondsLeft > 0}
              className="h-auto px-0 py-0 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
            >
              {resending ? "Sending…" : secondsLeft > 0 ? `Resend code in ${formatTime(secondsLeft)}` : "Resend code"}
            </Button>
            <Link to="/auth" className="hover:text-foreground">Back to sign in</Link>
          </div>
          <div className="mt-6 rounded-2xl border border-border/60 bg-muted/30 p-4">
            <h2 className="mb-2 text-sm font-medium">Didn’t receive the code?</h2>
            <ul className="space-y-1.5 text-xs text-muted-foreground">
              <li>• Check your spam or junk folder.</li>
              <li>• Make sure the email address above is correct.</li>
              <li>• Wait a minute and tap <span className="font-medium text-foreground">Resend code</span>.</li>
            </ul>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <Link to="/support" className="text-primary hover:underline">Contact support</Link>
              <span className="text-muted-foreground">or email</span>
              <a href="mailto:support@cryptobullinvestments.com" className="text-primary hover:underline">support@cryptobullinvestments.com</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}